import React, { useCallback, useRef, useState } from 'react';

import { Checkbox } from '@sbu/components.checkbox';
import { EmptyState } from '@sbu/components.emptystate';
import { Motion } from '@sbu/components.motion';
import { Pagination } from '@sbu/components.pagination';

import {
  EntitySortableField,
  EntityListFragment as EntityType,
  SortDir,
} from 'core/graphql';
import { SortDirection } from 'shared/components/data/Sort';
import { PageLoader } from 'shared/components/elements/Loader';
import { ValueBasedFilterTypes } from 'shared/components/internal/Filter';
import { Collapse } from 'shared/components/layout/Collapse';
import { List } from 'shared/components/layout/List';
import {
  Selection,
  SelectionChange,
  SelectionState,
} from 'shared/components/utils/Selection';

import css from './Entities.module.scss';
import { EntitiesBulkActions } from './EntitiesBulkActions';
import { EntitiesHeader } from './EntitiesHeader';
import { EntityRow } from './EntityRow';
import { ENTITY_QUERY_LIMIT } from './utils';

interface EntitiesProps {
  entities: EntityType[];
  loading: boolean;
  offset: number;
  query: string;
  sort: EntitySortableField;
  sortDir: SortDir;
  threshold?: number | null;
  total: number;
  onOffsetChange: (offset: number) => void;
  onQueryChange: (query: string) => void;
  onSortChange?: (sorts: SortDirection<EntitySortableField>[]) => void;
  onQuickFilter?: (filter: ValueBasedFilterTypes) => void;
}

export const Entities = ({
  entities,
  loading,
  offset,
  threshold,
  onOffsetChange,
  onQueryChange,
  onQuickFilter,
  onSortChange,
  query,
  sort,
  sortDir,
  total,
}: EntitiesProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const handleOffsetChange = (offset: number) => {
    onOffsetChange(offset);
    if (containerRef.current) {
      containerRef.current.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }
  };

  const [selectionState, setSelectionState] = useState<SelectionState>('none');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const updateSelection = useCallback(() => {
    // Determine the new selection.
    const isChecked = ['page', 'all'].includes(selectionState);
    const ids = isChecked ? [] : entities.map(({ id }) => id);

    // Update the state.
    setSelectedIds(ids);
    setSelectionState(isChecked ? 'none' : 'page');
  }, [entities, selectionState]);

  const handleSelectionChange = useCallback(
    ({ selections }: SelectionChange<string>) => {
      let selectedState: SelectionState = 'none';

      if (entities?.length && selections.length) {
        selectedState = entities.some(({ id }) => !selections.includes(id))
          ? 'partial'
          : 'page';
      }

      setSelectedIds(selections);
      setSelectionState(selectedState);
    },
    [entities]
  );

  return (
    <div className={css.container} ref={containerRef}>
      <EntitiesHeader
        onQueryChange={onQueryChange}
        onSortChange={onSortChange}
        query={query}
        sort={sort}
        sortDir={sortDir}
        total={total}
      />
      {!!total && (
        <div className={css.bulkActions}>
          <div className={css.checkboxContainer}>
            <Checkbox
              CheckboxProps={{
                checked: selectionState !== 'none',
                indeterminate: selectionState === 'partial',
                onChange: updateSelection,
                sx: { padding: 0 },
              }}
              hideLabel
              label='Select all entities'
            />
          </div>
          <Collapse expanded={!!selectedIds.length} horizontal={true}>
            <EntitiesBulkActions entityIds={selectedIds} total={total} />
          </Collapse>
        </div>
      )}
      {loading && <PageLoader />}
      {!loading && (
        <>
          {!total && <EmptyState />}
          <Selection
            mode='multiple'
            onSelectionChange={handleSelectionChange}
            selections={selectedIds}
          >
            <List>
              <Motion type='group'>
                {entities.map((entity) => (
                  <Motion
                    className={css.rowContainer}
                    key={`entity-${entity.id}`}
                    type='item'
                  >
                    <EntityRow
                      entity={entity}
                      threshold={threshold}
                      onQuickFilter={onQuickFilter}
                    />
                  </Motion>
                ))}
              </Motion>
            </List>
          </Selection>
          <Pagination
            limit={ENTITY_QUERY_LIMIT}
            offset={offset}
            onOffsetChange={handleOffsetChange}
            total={total}
          />
        </>
      )}
    </div>
  );
};
